import { useCallback, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  IconButton,
  Menu,
  MenuItem,
} from "@mui/material";
import CloseRounded from "@mui/icons-material/CloseRounded";
import CreditCardOutlinedIcon from "@mui/icons-material/CreditCardOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import KeyboardArrowDownRounded from "@mui/icons-material/KeyboardArrowDownRounded";
import LinkOutlined from "@mui/icons-material/LinkOutlined";
import SendOutlinedIcon from "@mui/icons-material/SendOutlined";

import Flx from "../../../components/layout/Flx";
import Txt from "../../../components/typography/Txt";
import AssociationsEditor from "../../../components/associations/AssociationsEditor";
import { useLoanFilesHook } from "../../../hooks/useLoanFilesHook";
import { loanDrilldownSet } from "../../../redux/actions/loanDrilldownActions";
import { formatBorrowerObjectFullName } from "../../../_src_shared/utils/functions/formatBorrowerObjectFullName";
import tpoColors, { status as tpoStatus } from "../../../tpo/theme/colors";
import SendToBorrowerScreen from "../../../tpo/screens/SendToBorrowerScreen/SendToBorrowerScreen";
import { buildLoanBorrowerPrefill } from "../loanBorrowerPrefill";

/**
 * The applicant card's action dropdown: send this borrower a loan-application
 * link, send them a credit-authorization link, or link loan documents already
 * on the loan to them.
 *
 * The two send actions open the ported SendToBorrowerScreen *in a modal*
 * (`embedded` + `hideHeader`) rather than navigating to /send/:linkType — the
 * user is mid-review on a loan, and minting a link should not cost them their
 * place. The screen is prefilled from the loan and this borrower, so the common
 * case is one click to the recipient field and Generate.
 *
 * The third action opens the same AssociationsEditor that AssignLoanFilesButton
 * uses; the save path is that button's, kept in step deliberately.
 */

const SEND_ACTIONS = [
  {
    key: "loan-application",
    label: "Send loan application",
    description: "A link to the full application, prefilled from this loan.",
    icon: DescriptionOutlinedIcon,
    tone: "info",
  },
  {
    key: "credit-auth",
    label: "Send credit authorization",
    description: "Request e-signed consent to pull this borrower's credit.",
    icon: CreditCardOutlinedIcon,
    tone: "success",
  },
];

const ApplicantSendActionsButton = ({ borrower, size = "small", sx }) => {
  const dispatch = useDispatch();
  const loanDrilldown = useSelector((state) => state.loanDrilldown);
  const { loading, associateDocumentWithObjects } = useLoanFilesHook();

  const [anchor, setAnchor] = useState(null);
  // Which send modal is open, keyed by SEND_ACTIONS `key` — null when closed.
  const [sendKind, setSendKind] = useState(null);
  const [assignOpen, setAssignOpen] = useState(false);

  const borrowerName = formatBorrowerObjectFullName(borrower) || "this borrower";

  const prefill = useMemo(
    () => buildLoanBorrowerPrefill(loanDrilldown, borrower || {}),
    [loanDrilldown, borrower],
  );

  // Pre-checked + locked rows in the editor: the loanDocument ids already
  // associated with this borrower.
  const existing = useMemo(
    () => ({ loanDocuments: borrower?.associations?.loanDocuments || [] }),
    [borrower],
  );

  const handleSaveAssociations = useCallback(
    (selected) => {
      const newDocIds = selected?.loanDocuments || [];
      if (newDocIds.length === 0) {
        setAssignOpen(false);
        return;
      }
      associateDocumentWithObjects({
        dealId: loanDrilldown?._id,
        loanDocumentsIds: newDocIds,
        borrowerIds: borrower?._id ? [borrower._id] : undefined,
        onSuccessFn: (updatedLoan) => {
          dispatch(loanDrilldownSet(updatedLoan));
          setAssignOpen(false);
        },
      });
    },
    [loanDrilldown?._id, borrower?._id, associateDocumentWithObjects, dispatch],
  );

  const openMenu = (e) => {
    // The card itself is a click target (it opens the borrower detail), so
    // every control in here has to stop the event reaching it.
    e.stopPropagation();
    setAnchor(e.currentTarget);
  };

  const activeAction = SEND_ACTIONS.find((a) => a.key === sendKind);

  return (
    // Everything this component renders is wrapped in one click trap. The menu
    // and both modals are portaled out of the card's DOM, but React replays
    // synthetic events up the *React* tree regardless of where the portal lands
    // — so without this, ticking a checkbox in the assign-files dialog also
    // fires the card's onClick and slides the borrower sidebar open behind it.
    <Box
      onClick={(e) => e.stopPropagation()}
      sx={{ display: "inline-flex", flexShrink: 0 }}
    >
      <Button
        variant="outlined"
        size={size}
        onClick={openMenu}
        startIcon={<SendOutlinedIcon sx={{ transform: "rotate(-45deg)" }} />}
        endIcon={
          <KeyboardArrowDownRounded
            sx={{
              fontSize: 18,
              ml: -0.5,
              transition: "transform 0.15s",
              transform: anchor ? "rotate(180deg)" : "none",
            }}
          />
        }
        sx={{
          flexShrink: 0,
          borderRadius: 999,
          px: 1.5,
          py: 0.5,
          fontSize: 12,
          fontWeight: 600,
          whiteSpace: "nowrap",
          textTransform: "none",
          color: tpoColors.slate700,
          borderColor: tpoColors.slate300,
          "&:hover": {
            borderColor: tpoColors.slate400,
            backgroundColor: tpoColors.slate50,
          },
          ...sx,
        }}
      >
        Send
      </Button>

      <Menu
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        disableScrollLock
        onClick={(e) => e.stopPropagation()}
        MenuListProps={{ sx: { py: 0 } }}
        slotProps={{
          paper: {
            elevation: 0,
            sx: {
              mt: 1,
              width: 372,
              maxWidth: "calc(100vw - 32px)",
              p: 1,
              borderRadius: "16px",
              border: `1px solid ${tpoColors.slate200}`,
              boxShadow: "0 18px 44px rgba(20,40,46,0.18)",
            },
          },
        }}
      >
        <Box
          sx={{
            px: 1.5,
            pt: 1,
            pb: 0.75,
            fontSize: 11.5,
            fontWeight: 700,
            letterSpacing: "0.06em",
            textTransform: "uppercase",
            color: tpoColors.slate400,
          }}
        >
          Send to {borrowerName}
        </Box>

        {SEND_ACTIONS.map((item) => {
          const Icon = item.icon;
          const tint = tpoStatus[item.tone] || tpoStatus.neutral;
          return (
            <MenuItem
              key={item.key}
              onClick={() => {
                setAnchor(null);
                setSendKind(item.key);
              }}
              disableRipple
              // whiteSpace: normal lets the description wrap — MenuItem forces
              // nowrap by default and would clip it to one line.
              sx={{
                alignItems: "flex-start",
                gap: 1.5,
                p: 1.5,
                borderRadius: "12px",
                whiteSpace: "normal",
                "&:hover": { backgroundColor: tpoColors.slate50 },
              }}
            >
              <Flx
                ac
                jc="center"
                sx={{
                  flexShrink: 0,
                  width: 40,
                  height: 40,
                  borderRadius: "10px",
                  backgroundColor: tint.bg,
                }}
              >
                <Icon
                  sx={{
                    fontSize: "20px !important",
                    color: `${tint.fg} !important`,
                  }}
                />
              </Flx>
              <Flx column g={0.25} sx={{ minWidth: 0 }}>
                <Box
                  sx={{
                    fontSize: 15,
                    fontWeight: 700,
                    color: tpoColors.slate900,
                  }}
                >
                  {item.label}
                </Box>
                <Box
                  sx={{
                    fontSize: 13.5,
                    lineHeight: 1.4,
                    color: tpoColors.slate500,
                  }}
                >
                  {item.description}
                </Box>
              </Flx>
            </MenuItem>
          );
        })}

        {/* Not a send — it links documents the loan already holds. Same menu
            because it is the same question ("what do I do with this
            borrower?"), separated by its own tint so it doesn't read as a
            third thing to email out. */}
        <MenuItem
          onClick={() => {
            setAnchor(null);
            setAssignOpen(true);
          }}
          disableRipple
          sx={{
            alignItems: "flex-start",
            gap: 1.5,
            p: 1.5,
            mt: 0.25,
            borderTop: `1px solid ${tpoColors.slate200}`,
            borderRadius: "12px",
            whiteSpace: "normal",
            "&:hover": { backgroundColor: tpoColors.slate50 },
          }}
        >
          <Flx
            ac
            jc="center"
            sx={{
              flexShrink: 0,
              width: 40,
              height: 40,
              borderRadius: "10px",
              backgroundColor: tpoStatus.neutral.bg,
            }}
          >
            <LinkOutlined
              sx={{
                fontSize: "20px !important",
                color: `${tpoStatus.neutral.fg} !important`,
              }}
            />
          </Flx>
          <Flx column g={0.25} sx={{ minWidth: 0 }}>
            <Box
              sx={{ fontSize: 15, fontWeight: 700, color: tpoColors.slate900 }}
            >
              Link existing loan documents
            </Box>
            <Box
              sx={{
                fontSize: 13.5,
                lineHeight: 1.4,
                color: tpoColors.slate500,
              }}
            >
              Attach documents already on this loan to this borrower.
            </Box>
          </Flx>
        </MenuItem>
      </Menu>

      {/* Send modal — the full send screen, minus its own title block. */}
      {/* White, unpadded, and capped at the same 1080px the standalone screen
          uses, so the embedded form keeps its column width instead of being
          squeezed into a dialog-sized one. Sized to its content rather than the
          viewport — a short credit-auth send shouldn't open full height. The
          screen drops its own card chrome when `embedded`; this dialog is the
          card. */}
      <Dialog
        open={Boolean(sendKind)}
        onClose={() => setSendKind(null)}
        maxWidth={false}
        scroll="body"
        onClick={(e) => e.stopPropagation()}
        slotProps={{
          paper: {
            sx: {
              width: "100%",
              maxWidth: 1080,
              m: 2,
              borderRadius: "16px",
              backgroundColor: tpoColors.white,
            },
          },
        }}
      >
        <Flx
          ac
          sx={{
            justifyContent: "space-between",
            px: 3,
            pt: 2.5,
            pb: 1.5,
            gap: 2,
          }}
        >
          <Flx column g={0.25} sx={{ minWidth: 0 }}>
            <Txt
              component="div"
              sx={{ fontSize: 18, fontWeight: 700, color: tpoColors.slate900 }}
            >
              {activeAction?.label || "Send to borrower"}
            </Txt>
            <Txt
              component="div"
              sx={{ fontSize: 13, color: tpoColors.slate500 }}
            >
              Prefilled from this loan and {borrowerName}.
            </Txt>
          </Flx>
          <IconButton size="small" onClick={() => setSendKind(null)}>
            <CloseRounded sx={{ fontSize: 20 }} />
          </IconButton>
        </Flx>
        {/* Fully unpadded. The embedded screen's own FormSection padding is what
            aligns its fields and separates its sections; a second layer of
            dialog padding just inset the whole form and pushed it off-centre
            against the header above. */}
        <DialogContent sx={{ p: "0 !important" }}>
          {sendKind && (
            <SendToBorrowerScreen
              embedded
              hideHeader
              linkType={sendKind}
              borrowerPrefill={prefill}
              // "Done" on the result card closes the dialog — routed, the
              // screen navigates instead, which would take the user off the
              // loan they are working.
              onDone={() => setSendKind(null)}
            />
          )}
        </DialogContent>
      </Dialog>

      <AssociationsEditor
        open={assignOpen}
        loan={loanDrilldown}
        enabledFields={["loanDocuments"]}
        existing={existing}
        title="Assign files to borrower"
        hostLabel={formatBorrowerObjectFullName(borrower) || "Borrower"}
        saving={loading}
        onSave={handleSaveAssociations}
        onClose={() => setAssignOpen(false)}
      />
    </Box>
  );
};

export default ApplicantSendActionsButton;
